import { useMemo, useRef } from 'react';
function createStableCell(dataKey, latest) {
    // Called, not mounted: a cell's own hooks stay attached to this wrapper instead of moving with
    // the closure the surface rebuilt.
    function Cell(props) {
        const render = latest.current.get(dataKey);
        return render ? render(props) : null;
    }
    Cell.displayName = `StableCell(${dataKey})`;
    return Cell;
}
/**
 * Keeps one component identity per column for the life of the surface.
 *
 * `getCellTemplate` passes `column.renderer` to `React.createElement` as the element TYPE, and the
 * surfaces rebuild their columns whenever the fetched rows change (Building's category options are
 * derived from them). A new function identity is a new type, so React unmounted and remounted
 * EVERY cell on every refetch.
 *
 * That is what kills the dropdown in task-11: renaming a row inline refetches the listing, the
 * remount lands while the Category dropdown the user just opened is still mounted, and it takes
 * the dropdown's `visible` state with it. The same remount restarted every PDF preview in the
 * table.
 */
export function useStableCellRenderers(columns) {
    const latest = useRef(new Map());
    const stable = useRef(new Map());
    return useMemo(() => columns.map((column) => {
        if (!column.renderer)
            return column;
        const { dataKey } = column;
        latest.current.set(dataKey, column.renderer);
        if (!stable.current.has(dataKey)) {
            stable.current.set(dataKey, createStableCell(dataKey, latest));
        }
        return Object.assign(Object.assign({}, column), { renderer: stable.current.get(dataKey) });
    }), [columns]);
}
export default useStableCellRenderers;
